import { useState, useEffect, useMemo, useCallback } from 'react';
import { useQuery } from '@apollo/client';
import { GET_WORK_ORDERS } from '../graphql/queries';

const WORK_ORDERS_PER_PAGE = 100;

export function useMyAssignedWorkOrders(user, searchTerm = '') {
  const [accumulated, setAccumulated] = useState([]);
  const [page, setPage] = useState(1);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const effectiveSearch = searchTerm && searchTerm.length >= 2 ? searchTerm : undefined;

  const { loading, error, data, refetch, fetchMore } = useQuery(GET_WORK_ORDERS, {
    variables: {
      input: {
        assignedToMe: true,
        statuses: ['OPEN', 'IN_PROGRESS'],
        search: effectiveSearch,
        sortOrder: 'recent',
        limit: WORK_ORDERS_PER_PAGE,
        page: 1,
      },
    },
    skip: !user?.id,
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: false,
    onCompleted: (result) => {
      const items = result?.workOrders?.items || [];
      setAccumulated(items);
      setPage(1);
    },
  });

  useEffect(() => {
    setAccumulated([]);
    setPage(1);
  }, [user?.id, effectiveSearch]);

  const total = data?.workOrders?.total || 0;
  const hasMore = accumulated.length < total;

  const loadMore = useCallback(async () => {
    if (isLoadingMore || !hasMore) return;
    setIsLoadingMore(true);
    try {
      const nextPage = page + 1;
      const result = await fetchMore({
        variables: {
          input: {
            assignedToMe: true,
            statuses: ['OPEN', 'IN_PROGRESS'],
            search: effectiveSearch,
            sortOrder: 'recent',
            limit: WORK_ORDERS_PER_PAGE,
            page: nextPage,
          },
        },
      });
      const newItems = result?.data?.workOrders?.items || [];
      if (newItems.length > 0) {
        setAccumulated((prev) => {
          const seen = new Set(prev.map((wo) => wo.id));
          const deduped = newItems.filter((wo) => !seen.has(wo.id));
          return [...prev, ...deduped];
        });
        setPage(nextPage);
      }
    } catch (err) {
      console.error('Error loading more work orders:', err);
    } finally {
      setIsLoadingMore(false);
    }
  }, [isLoadingMore, hasMore, page, fetchMore, effectiveSearch]);

  const { openOrders, inProgressOrders } = useMemo(() => {
    const open = [];
    const inProgress = [];
    accumulated.forEach((wo) => {
      if (wo.status === 'OPEN') open.push(wo);
      else if (wo.status === 'IN_PROGRESS') inProgress.push(wo);
    });
    return { openOrders: open, inProgressOrders: inProgress };
  }, [accumulated]);

  const refresh = useCallback(async () => {
    setAccumulated([]);
    setPage(1);
    try {
      await refetch();
    } catch (err) {
      console.error('Error refreshing assigned work orders:', err);
    }
  }, [refetch]);

  return {
    openOrders,
    inProgressOrders,
    total,
    loading,
    error,
    refetch: refresh,
    hasMore,
    isLoadingMore,
    loadMore,
  };
}
